import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { Send, MessageCircle } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';

const ChatInterface = ({ orderId, orderType, otherPartyName, height = '400px' }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!orderId) return;
    loadMessages();
    const interval = setInterval(loadMessages, 4000); // Poll every 4 seconds
    return () => clearInterval(interval);
  }, [orderId, orderType]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadMessages = async () => {
    try {
      const response = await axios.get(`${API}/chat/${orderType}/${orderId}`, { withCredentials: true });
      setMessages(response.data);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      await axios.post(`${API}/chat/send`, {
        order_id: orderId,
        order_type: orderType,
        message: newMessage.trim()
      }, { withCredentials: true });
      setNewMessage('');
      loadMessages();
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="glass-card rounded-2xl flex flex-col" style={{ height }} data-testid="chat-interface">
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
        <div className="p-1.5 bg-gradient-to-r from-[#FF007F] to-[#8A2BE2] rounded-lg">
          <MessageCircle className="w-4 h-4 text-white" />
        </div>
        <div>
          <p className="text-sm font-medium text-white" style={{ fontFamily: 'Outfit' }}>
            {otherPartyName || 'Chat'}
          </p>
          <p className="text-xs text-[#9CA3AF]">Actualización automática cada 4s</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="h-full flex items-center justify-center text-center">
            <div>
              <MessageCircle className="w-12 h-12 text-[#9CA3AF]/40 mx-auto mb-2" />
              <p className="text-sm text-[#9CA3AF]">Aún no hay mensajes</p>
              <p className="text-xs text-[#9CA3AF]/60 mt-1">Envía el primer mensaje</p>
            </div>
          </div>
        ) : (
          messages.map((msg) => {
            const isMe = msg.sender_id === user._id;
            return (
              <div
                key={msg.id}
                data-testid={`chat-message-${msg.id}`}
                className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[75%] px-4 py-2 rounded-2xl ${
                  isMe
                    ? 'bg-gradient-to-r from-[#FF007F] to-[#8A2BE2] text-white'
                    : 'bg-white/5 border border-white/10 text-white'
                }`}>
                  {!isMe && (
                    <p className="text-xs text-[#9CA3AF] mb-1">{msg.sender_name}</p>
                  )}
                  <p className="text-sm">{msg.message}</p>
                  <p className={`text-xs mt-1 ${isMe ? 'text-white/60' : 'text-[#9CA3AF]/60'}`}>
                    {new Date(msg.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="p-3 border-t border-white/10 flex gap-2">
        <Input
          data-testid="chat-message-input"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Escribe un mensaje..."
          className="flex-1 bg-white/5 border-white/10 text-white rounded-xl"
          disabled={sending}
        />
        <Button
          type="submit"
          data-testid="chat-send-button"
          disabled={!newMessage.trim() || sending}
          className="gradient-button rounded-xl px-4"
        >
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
};

export default ChatInterface;
